import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Text,
  Flex,
  HStack,
  VStack,
  Link,
  Avatar,
  Textarea,
  IconButton,
} from '@chakra-ui/react';
import { AddIcon, CheckIcon, EditIcon } from '@chakra-ui/icons';
import type { Influencer } from '@foodfluence/shared';
import { getInfluencerFirstName, getInfluencerDisplayName } from '../../../../../../utils/influencerUtils';
import { ff } from '../../../../../../theme';
import MarketplaceTokenIcon from '../MarketplaceTokenIcon';

const BIO_PLACEHOLDER = 'Add a public bio';
const PROFILE_IMAGE_INPUT_ID = 'lc-marketplace-profile-image-input';

function getInfluencerFullName(influencer: Partial<Influencer> | null | undefined): string {
  if (!influencer) return 'Creator';
  const first = getInfluencerFirstName(influencer);
  const last =
    influencer.lastName &&
    influencer.lastName.trim() !== '' &&
    influencer.lastName.toLowerCase() !== 'unknown'
      ? influencer.lastName.trim()
      : '';
  if (first && last) return `${first} ${last}`;
  if (first) return first;
  return getInfluencerDisplayName(influencer);
}

export interface LocalProfileSectionProps {
  influencer: Partial<Influencer> | null;
  /** Marketplace-specific photo; falls back to `influencer.profile_pic_url` when unset. */
  displayAvatarUrl?: string | null;
  bio: string;
  onBioChange: (value: string) => void;
  onOpenAboutLocalCreators?: () => void;
  /** Persists public bio only (at most 750 characters). Returns true when a save was written to the server. */
  onSaveBio?: () => boolean | Promise<boolean>;
  isSavingBio?: boolean;
  /** When the marketplace has its own image, “Use account photo” clears it. */
  hasMarketplaceProfileImage?: boolean;
  onSelectProfileImageFile?: (file: File) => void;
  isUploadingProfileImage?: boolean;
  onClearMarketplaceProfileImage?: () => void;
}

const LocalProfileSection: React.FC<LocalProfileSectionProps> = ({
  influencer,
  displayAvatarUrl,
  bio,
  onBioChange,
  onOpenAboutLocalCreators,
  onSaveBio,
  isSavingBio = false,
  hasMarketplaceProfileImage = false,
  onSelectProfileImageFile,
  isUploadingProfileImage = false,
  onClearMarketplaceProfileImage,
}) => {
  const displayName = getInfluencerFullName(influencer);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isEditingBio, setIsEditingBio] = useState(false);

  const avatarSrc = (displayAvatarUrl ?? influencer?.profile_pic_url) || undefined;

  const onProfileImageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !onSelectProfileImageFile) {
      return;
    }
    onSelectProfileImageFile(f);
  };

  useEffect(() => {
    if (!isEditingBio) {
      return;
    }
    const id = requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [isEditingBio]);

  return (
    <Box as="section" aria-label="Profile">
      <HStack justify="space-between" align="center" mb="4px" spacing={2}>
        <Text as="h3" textStyle="eyebrow" color={ff.text2} flex={1} m={0}>
          Profile
        </Text>
        {onOpenAboutLocalCreators ? (
          <Box
            as="button"
            type="button"
            display="flex"
            alignItems="center"
            gap="4px"
            bg="none"
            border="none"
            p={0}
            minH="44px"
            cursor="pointer"
            onClick={onOpenAboutLocalCreators}
            aria-label="About Local Creators"
            _hover={{ opacity: 0.85 }}
          >
            <MarketplaceTokenIcon size={11} />
            <Text fontSize="12px" fontWeight={600} color={ff.ink} textDecor="underline">
              About LocalCreators
            </Text>
          </Box>
        ) : (
          <Link
            href="https://www.foodfluence.com"
            isExternal
            display="flex"
            alignItems="center"
            gap="4px"
            minH="44px"
            _hover={{ opacity: 0.85 }}
          >
            <MarketplaceTokenIcon size={11} />
            <Text fontSize="12px" fontWeight={600} color={ff.ink} textDecor="underline">
              About LocalCreators
            </Text>
          </Link>
        )}
      </HStack>

      <Box
        border="1px solid"
        borderColor={ff.line}
        borderRadius="20px"
        overflow="hidden"
        bg="white"
      >
        <Flex
          p="16px"
          align="flex-start"
          position="relative"
          gap="12px"
        >
          <VStack align="center" flexShrink={0} spacing="6px" pt={0}>
            {onSelectProfileImageFile != null ? (
              <Box
                as="label"
                position="relative"
                display="inline-block"
                w="60px"
                h="60px"
                flexShrink={0}
                cursor={isUploadingProfileImage ? 'not-allowed' : 'pointer'}
                pointerEvents={isUploadingProfileImage ? 'none' : 'auto'}
                opacity={isUploadingProfileImage ? 0.5 : 1}
                aria-label="Add or change local creators profile photo"
                _hover={isUploadingProfileImage ? undefined : { opacity: 0.92 }}
              >
                <input
                  id={PROFILE_IMAGE_INPUT_ID}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
                  disabled={isUploadingProfileImage}
                  onChange={onProfileImageInputChange}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'inherit',
                    zIndex: 2,
                  }}
                />
                <Box
                  position="absolute"
                  top="2px"
                  left="2px"
                  w="56px"
                  h="56px"
                  borderRadius="full"
                  overflow="hidden"
                  bg={ff.grey}
                  pointerEvents="none"
                  zIndex={0}
                >
                  <Avatar
                    src={avatarSrc}
                    name={displayName}
                    w="100%"
                    h="100%"
                    showBorder={false}
                    fontSize="22px"
                    fontWeight="600"
                    color={ff.text2}
                    bg="transparent"
                  />
                </Box>
                <Box
                  position="absolute"
                  right="0"
                  bottom="0"
                  w="22px"
                  h="22px"
                  borderRadius="full"
                  border="2px solid white"
                  bg={ff.lime}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  pointerEvents="none"
                  zIndex={1}
                  aria-hidden
                >
                  <AddIcon boxSize="10px" color={ff.ink} />
                </Box>
              </Box>
            ) : (
              <Box
                w="56px"
                h="56px"
                borderRadius="full"
                overflow="hidden"
                bg={ff.grey}
              >
                <Avatar
                  src={avatarSrc}
                  name={displayName}
                  w="100%"
                  h="100%"
                  showBorder={false}
                  fontSize="22px"
                  fontWeight="600"
                  color={ff.text2}
                  bg="transparent"
                />
              </Box>
            )}
            {hasMarketplaceProfileImage && onClearMarketplaceProfileImage && (
              <Text
                as="button"
                type="button"
                fontSize="12px"
                color={ff.text2}
                fontWeight={500}
                lineHeight="1.2"
                textAlign="center"
                maxW="72px"
                p={0}
                border="none"
                bg="none"
                cursor={isUploadingProfileImage ? 'not-allowed' : 'pointer'}
                textDecor="underline"
                opacity={isUploadingProfileImage ? 0.4 : 1}
                onClick={() => {
                  if (isUploadingProfileImage) return;
                  onClearMarketplaceProfileImage();
                }}
                _hover={isUploadingProfileImage ? undefined : { color: ff.ink }}
              >
                Use account photo
              </Text>
            )}
          </VStack>
          <VStack align="flex-start" flex={1} minW={0} spacing={0} pt="2px">
            <Text as="h4" textStyle="display" fontSize="20px" color={ff.ink} noOfLines={1}>
              {displayName}
            </Text>
            <Text fontSize="13px" lineHeight="1.3" color={ff.text2} mt="4px">
              Creator
            </Text>
          </VStack>
        </Flex>
        <Box
          borderTop="1px solid"
          borderColor={ff.line}
          px="16px"
          py="12px"
        >
          {onSaveBio == null ? (
            <Textarea
              ref={textareaRef}
              value={bio}
              onChange={(e) => onBioChange(e.target.value)}
              minH="85px"
              variant="unstyled"
              p={0}
              fontSize="15px"
              lineHeight="22.5px"
              color={ff.body}
              fontWeight="400"
              resize="vertical"
              _placeholder={{ color: ff.muted }}
              placeholder={BIO_PLACEHOLDER}
              aria-label="Public bio"
            />
          ) : isEditingBio ? (
            <Box>
              <Textarea
                ref={textareaRef}
                value={bio}
                onChange={(e) => onBioChange(e.target.value)}
                minH="85px"
                variant="unstyled"
                p={0}
                fontSize="15px"
                lineHeight="22.5px"
                color={ff.body}
                fontWeight="400"
                resize="vertical"
                _placeholder={{ color: ff.muted }}
                placeholder={BIO_PLACEHOLDER}
                aria-label="Edit public bio"
              />
              <Flex justify="flex-end" pt="10px" mt="8px" borderTop="1px solid" borderColor={ff.line}>
                <IconButton
                  type="button"
                  aria-label="Save public bio and stop editing"
                  w="44px"
                  h="44px"
                  minW="44px"
                  borderRadius="full"
                  bg={ff.lime}
                  color={ff.ink}
                  _hover={{ opacity: 0.9 }}
                  isLoading={isSavingBio}
                  icon={<CheckIcon boxSize="18px" />}
                  onClick={async () => {
                    if (!onSaveBio) return;
                    const ok = await onSaveBio();
                    if (ok) {
                      setIsEditingBio(false);
                    }
                  }}
                />
              </Flex>
            </Box>
          ) : (
            <Box>
              <Textarea
                value={bio.trim() ? bio : ''}
                readOnly
                minH="85px"
                maxH="85px"
                overflowY="auto"
                resize="none"
                variant="unstyled"
                p={0}
                fontSize="15px"
                lineHeight="22.5px"
                color={bio.trim() ? ff.body : ff.muted}
                fontWeight="400"
                _placeholder={{ color: ff.muted }}
                placeholder={BIO_PLACEHOLDER}
                cursor="default"
                _focus={{ boxShadow: 'none' }}
                _focusVisible={{ boxShadow: 'none' }}
                aria-label="Public bio"
                tabIndex={-1}
              />
              <Flex justify="flex-end" pt="10px" mt="8px" borderTop="1px solid" borderColor={ff.line}>
                <IconButton
                  type="button"
                  aria-label="Edit public bio"
                  w="44px"
                  h="44px"
                  minW="44px"
                  variant="ghost"
                  color={ff.ink}
                  borderRadius="full"
                  _hover={{ bg: ff.grey }}
                  icon={<EditIcon boxSize="16px" />}
                  onClick={() => {
                    setIsEditingBio(true);
                  }}
                />
              </Flex>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default LocalProfileSection;
